import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "@/lib/api";
import { formatKst } from "@/lib/datetime";
import { formatWon } from "@/lib/quote";
import type {
  FunnelResponseDTO,
  SourcesResponseDTO,
  VisitListItemDTO,
  VisitListResponseDTO,
  VisitSessionDTO,
  VisitStage,
} from "@/types/visit";

const DAY_OPTIONS = [7, 30, 90] as const;
const PAGE_SIZE = 20;

const DEVICE_LABEL: Record<string, string> = { mobile: "모바일", tablet: "태블릿", desktop: "데스크톱" };

// api `STAGE_LABEL`(api/models/visit.py) 과 같은 단건 이벤트 이름이에요 — 퍼널 행의 "…이상" 누적
// 라벨(FUNNEL_LABEL, 서버가 내려줌)과는 다른 용도라 여기 별도로 정의해요.
const STAGE_LABEL: Record<VisitStage, string> = {
  landed: "랜딩",
  viewed_about: "소개 도달",
  viewed_portfolio: "포트폴리오 도달",
  portfolio_expanded: "포트폴리오 펼침",
  portfolio_link: "포트폴리오 링크 클릭",
  viewed_quote: "견적 도달",
  quote_option: "견적 옵션 선택",
  quote_preset: "견적 프리셋 선택",
  quote_computed: "견적 산정",
  quote_process_opened: "진행 절차 열람",
  inquiry_opened: "문의 진입",
  inquiry_submitted: "문의 제출",
};

function formatTime(iso: string): string {
  return formatKst(iso, { dateStyle: "short", timeStyle: "short" });
}

function SessionDetail({
  token,
  sessionId,
  onLogout,
}: {
  token: string;
  sessionId: string;
  onLogout: () => void;
}) {
  const [detail, setDetail] = useState<VisitSessionDTO | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setDetail(null);
    setError("");
    api
      .get<VisitSessionDTO>(`/admin/visits/${sessionId}`, token)
      .then((data) => {
        if (alive) setDetail(data);
      })
      .catch((err) => {
        if (!alive) return;
        if (err instanceof ApiError && err.status === 401) {
          onLogout();
          return;
        }
        setError(err instanceof ApiError ? err.message : "상세를 불러오지 못했어요.");
      });
    return () => {
      alive = false;
    };
  }, [sessionId, token, onLogout]);

  if (error) return <output className="wds-caption-1 text-status-negative">{error}</output>;
  if (!detail) return <output className="wds-caption-1 text-label-assistive">불러오는 중이에요</output>;

  return (
    <div className="flex flex-col gap-4 bg-background-alternative p-4">
      <div className="flex flex-col gap-1.5">
        <span className="wds-label-2 text-label-strong">이벤트 타임라인</span>
        <ol className="flex flex-col gap-1">
          {detail.events.map((event, index) => (
            <li key={`${event.stage}-${event.at}-${index}`} className="flex gap-3 wds-caption-1">
              <span className="tabular-nums text-label-assistive">{formatTime(event.at)}</span>
              <span className="text-label-normal">{STAGE_LABEL[event.stage] ?? event.stage}</span>
              {event.detail && <span className="text-label-assistive">{event.detail}</span>}
            </li>
          ))}
        </ol>
      </div>

      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 wds-caption-1">
        <dt className="text-label-assistive">유입 URL</dt>
        <dd className="break-all text-label-normal">{detail.referrer ?? "직접 방문"}</dd>
        {/* path_sequence 는 여정이 아니에요 — meta 는 문서 로드 첫 요청에만 실려서, SPA 내 이동에서는
            늘지 않고 전체 페이지 이동(예: 헤더의 /inquiry 링크)에서만 항목이 늘어요. 그래서 "방문 경로"가
            아니라 "진입 경로"로 표기하고, 순서가 있는 여정처럼 보이지 않게 해요. */}
        <dt className="text-label-assistive">진입 경로</dt>
        <dd className="text-label-normal">{detail.path_sequence.join(" · ") || detail.landing_path}</dd>
        <dt className="text-label-assistive">브라우저</dt>
        <dd className="text-label-normal">
          {detail.client.browser ?? "알 수 없음"} {detail.client.browser_version ?? ""} ·{" "}
          {detail.client.os ?? "알 수 없음"} {detail.client.os_version ?? ""}
        </dd>
        <dt className="text-label-assistive">화면</dt>
        <dd className="tabular-nums text-label-normal">
          {detail.client.viewport_w ?? "-"}×{detail.client.viewport_h ?? "-"} @{detail.client.dpr ?? "-"}x
        </dd>
        <dt className="text-label-assistive">언어·시간대</dt>
        <dd className="text-label-normal">
          {detail.client.language ?? "-"} · {detail.client.timezone ?? "-"}
        </dd>
        <dt className="text-label-assistive">최대 스크롤</dt>
        <dd className="tabular-nums text-label-normal">
          {detail.max_scroll_pct === null ? "-" : `${detail.max_scroll_pct}%`}
        </dd>
      </dl>

      {detail.quote && (
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 border-line-alternative border-t pt-3 wds-caption-1">
          <dt className="text-label-assistive">첫 견적 유형</dt>
          <dd className="text-label-normal">{detail.quote.type}</dd>
          <dt className="text-label-assistive">페이지 수</dt>
          <dd className="tabular-nums text-label-normal">{detail.quote.pages}</dd>
          <dt className="text-label-assistive">선택 옵션</dt>
          <dd className="text-label-normal">{detail.quote.option_keys.join(", ") || "없음"}</dd>
          <dt className="text-label-assistive">첫 견적 할인가</dt>
          <dd className="tabular-nums text-label-normal">{formatWon(detail.quote.discounted)}</dd>
        </dl>
      )}

      {detail.inquiry_id && (
        <p className="wds-caption-1 text-label-neutral">
          이 방문은 문의로 이어졌어요. 문의 ID <span className="tabular-nums">{detail.inquiry_id}</span>
        </p>
      )}
    </div>
  );
}

function VisitRow({
  item,
  token,
  open,
  onToggle,
  onLogout,
}: {
  item: VisitListItemDTO;
  token: string;
  open: boolean;
  onToggle: () => void;
  onLogout: () => void;
}) {
  return (
    <>
      <tr className="border-line-alternative border-b last:border-0">
        <td className="py-2">
          <button type="button" onClick={onToggle} aria-expanded={open} className="flex flex-col items-start gap-0.5">
            <span className="tabular-nums text-label-normal">{formatTime(item.started_at)}</span>
            <span className="wds-caption-2 tabular-nums text-label-assistive" title={item.session_id}>
              {item.session_id.slice(0, 8)}
            </span>
          </button>
        </td>
        <td className="py-2 text-label-normal">{item.utm_source ?? item.referrer_host ?? "직접 방문"}</td>
        <td className="py-2 text-label-neutral">{DEVICE_LABEL[item.device] ?? item.device}</td>
        <td className="py-2 text-label-neutral">
          {item.last_stage === null ? "-" : STAGE_LABEL[item.last_stage] ?? item.last_stage}
        </td>
        <td className="py-2 text-right tabular-nums text-label-normal">
          {item.quote_discounted === null ? "-" : formatWon(item.quote_discounted)}
        </td>
        <td className="py-2 text-right text-label-neutral">{item.converted ? "예" : "-"}</td>
      </tr>
      {open && (
        <tr>
          <td colSpan={6} className="p-0">
            <SessionDetail token={token} sessionId={item.session_id} onLogout={onLogout} />
          </td>
        </tr>
      )}
    </>
  );
}

export function VisitPanel({ token, onLogout }: { token: string; onLogout: () => void }) {
  const [days, setDays] = useState<number>(30);
  const [funnel, setFunnel] = useState<FunnelResponseDTO | null>(null);
  const [sources, setSources] = useState<SourcesResponseDTO | null>(null);
  const [list, setList] = useState<VisitListResponseDTO | null>(null);
  const [page, setPage] = useState(1);
  // 두 토글은 상호 배타적이에요(문의했으면서 동시에 미전환일 수 없어요) — 하나의 유니언 상태로
  // "둘 다 꺼짐"까지 셋 중 하나만 되게 해요.
  const [filter, setFilter] = useState<"all" | "converted" | "quote_dropped">("all");
  const [openId, setOpenId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const setDaysAndResetPage = (d: number) => {
    setDays(d);
    setPage(1);
  };

  const toggleFilter = (target: "converted" | "quote_dropped") => {
    setFilter((prev) => (prev === target ? "all" : target));
    setPage(1);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const query = new URLSearchParams({ days: String(days), page: String(page), size: String(PAGE_SIZE) });
      if (filter === "converted") query.set("converted", "true");
      if (filter === "quote_dropped") {
        query.set("stage", "quote_computed");
        query.set("converted", "false");
      }
      const [funnelData, sourcesData, listData] = await Promise.all([
        api.get<FunnelResponseDTO>(`/admin/visits/funnel?days=${days}`, token),
        api.get<SourcesResponseDTO>(`/admin/visits/sources?days=${days}`, token),
        api.get<VisitListResponseDTO>(`/admin/visits?${query.toString()}`, token),
      ]);
      setFunnel(funnelData);
      setSources(sourcesData);
      setList(listData);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onLogout();
        return;
      }
      setFunnel(null);
      setSources(null);
      setList(null);
      setError(err instanceof ApiError ? err.message : "방문 데이터를 불러오지 못했어요.");
    } finally {
      setLoading(false);
    }
  }, [filter, days, page, token, onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = list ? Math.max(1, Math.ceil(list.total / PAGE_SIZE)) : 1;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center gap-2">
        {DAY_OPTIONS.map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setDaysAndResetPage(d)}
            aria-pressed={days === d}
            className={`wds-label-1 rounded-md px-4 py-2 transition-colors duration-200 ease-standard ${
              days === d ? "bg-primary text-static-white" : "bg-background-alternative text-label-neutral"
            }`}
          >
            {d}일
          </button>
        ))}
        <button
          type="button"
          onClick={() => toggleFilter("converted")}
          aria-pressed={filter === "converted"}
          className={`rounded-full px-4 py-1.5 wds-label-2 ${
            filter === "converted" ? "bg-primary text-static-white" : "bg-background-alternative text-label-neutral"
          }`}
        >
          문의한 방문만
        </button>
        <button
          type="button"
          onClick={() => toggleFilter("quote_dropped")}
          aria-pressed={filter === "quote_dropped"}
          className={`rounded-full px-4 py-1.5 wds-label-2 ${
            filter === "quote_dropped"
              ? "bg-primary text-static-white"
              : "bg-background-alternative text-label-neutral"
          }`}
        >
          견적만 보고 이탈
        </button>
        {loading && <output className="wds-caption-1 text-label-assistive">불러오는 중이에요</output>}
      </div>

      {error && <output className="wds-body-2 text-status-negative">{error}</output>}

      <section className="overflow-x-auto rounded-lg bg-background-elevated p-6 shadow-normal">
        <h2 className="wds-label-1 text-label-strong">전환 퍼널</h2>
        <p className="mt-1 mb-4 wds-caption-1 text-label-assistive">
          각 행은 그 단계 이상 도달한 세션의 누적 집계예요.
        </p>
        <table className="w-full wds-body-2">
          <thead>
            <tr className="border-line-alternative border-b text-label-assistive">
              <th className="px-4 py-3 text-left font-medium">단계</th>
              <th className="px-4 py-3 text-right font-medium">세션</th>
              <th className="px-4 py-3 text-right font-medium">직전 단계 대비</th>
              <th className="px-4 py-3 text-right font-medium">이탈</th>
            </tr>
          </thead>
          <tbody>
            {funnel?.rows.map((row) => (
              <tr key={row.stage} className="border-line-alternative border-b last:border-0">
                <td className="px-4 py-3 text-label-normal">{row.label}</td>
                <td className="px-4 py-3 text-right tabular-nums text-label-normal">
                  {row.sessions.toLocaleString("ko-KR")}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-label-neutral">
                  {row.conversion_pct === null ? "—" : `${row.conversion_pct}%`}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-label-neutral">
                  {row.dropped === null ? "—" : row.dropped.toLocaleString("ko-KR")}
                </td>
              </tr>
            ))}
            {!loading && funnel && funnel.rows[0]?.sessions === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-label-assistive">
                  아직 기록된 방문이 없어요.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      <section className="flex flex-col gap-3 rounded-lg bg-background-elevated p-6 shadow-normal">
        <h3 className="wds-label-1 text-label-strong">유입 소스</h3>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[28rem] wds-body-2">
            <thead>
              <tr className="border-line-alternative border-b text-label-assistive">
                <th className="py-2 text-left font-normal">소스</th>
                <th className="py-2 text-right font-normal">세션</th>
                <th className="py-2 text-right font-normal">문의</th>
                <th className="py-2 text-right font-normal">전환율</th>
              </tr>
            </thead>
            <tbody>
              {sources?.rows.map((row) => (
                <tr key={row.source} className="border-line-alternative border-b last:border-0">
                  <td className="py-2 break-all text-label-normal">{row.source}</td>
                  <td className="py-2 text-right tabular-nums text-label-normal">
                    {row.sessions.toLocaleString("ko-KR")}
                  </td>
                  <td className="py-2 text-right tabular-nums text-label-normal">
                    {row.inquiries.toLocaleString("ko-KR")}
                  </td>
                  <td className="py-2 text-right tabular-nums text-label-neutral">{row.conversion_pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {sources && sources.rows.length === 0 && (
          <p className="wds-caption-1 text-label-assistive">아직 유입 기록이 없어요.</p>
        )}
      </section>

      <section className="flex flex-col gap-3 rounded-lg bg-background-elevated p-6 shadow-normal">
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="wds-label-1 text-label-strong">세션 목록</h3>
          <span className="wds-caption-1 tabular-nums text-label-assistive">
            {(list?.total ?? 0).toLocaleString("ko-KR")}건
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[46rem] wds-body-2">
            <thead>
              <tr className="border-line-alternative border-b text-label-assistive">
                <th className="py-2 text-left font-normal">시각</th>
                <th className="py-2 text-left font-normal">유입</th>
                <th className="py-2 text-left font-normal">기기</th>
                <th className="py-2 text-left font-normal">최근 이벤트</th>
                <th className="py-2 text-right font-normal">첫 견적</th>
                <th className="py-2 text-right font-normal">문의</th>
              </tr>
            </thead>
            <tbody>
              {list?.items.map((item) => (
                <VisitRow
                  key={item.session_id}
                  item={item}
                  token={token}
                  open={openId === item.session_id}
                  onToggle={() => setOpenId(openId === item.session_id ? null : item.session_id)}
                  onLogout={onLogout}
                />
              ))}
            </tbody>
          </table>
        </div>

        {list && list.items.length === 0 && (
          <p className="wds-caption-1 text-label-assistive">조건에 맞는 방문이 없어요.</p>
        )}

        {list && list.total > PAGE_SIZE && (
          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="rounded-lg bg-background-alternative px-3 py-1.5 wds-label-2 text-label-neutral disabled:opacity-40"
            >
              이전
            </button>
            <span className="wds-caption-1 tabular-nums text-label-assistive">
              {page} / {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-lg bg-background-alternative px-3 py-1.5 wds-label-2 text-label-neutral disabled:opacity-40"
            >
              다음
            </button>
          </div>
        )}
      </section>
    </div>
  );
}

export default VisitPanel;
